import {useLazyQuery} from "@apollo/client";
import {useCallback} from "react";
import toast from "react-hot-toast";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {GQL_ANALYZER_REPORT_PDF_QUERY} from "@/lib/queries/analyzer.ts";
import {PdfResult} from "@/lib/types/common.ts";
import {openPdfDownload} from "@/lib/utils/pdfUtils.ts";
import {MonitoringReport} from "@/pages/analyzer/Analyzer.types.ts";
import {buildReportPdfPayload} from "@/pages/analyzer/Analyzer.utils.ts";
import {useAnalyzerContext} from "@/pages/analyzer/hooks/AnalyzerContext.tsx";

export const useAnalyzerReportPdfDownload = () => {
  const {user} = useCurrentUserContext();
  const {proposedChecks, recommendedChecks, positionById, proposedGroups, getThreadId, accountUsage, formData} =
    useAnalyzerContext();
  const [fetchPdf, {loading}] = useLazyQuery<{analyzerReportPdf: PdfResult}>(GQL_ANALYZER_REPORT_PDF_QUERY, {
    fetchPolicy: "no-cache",
  });

  const downloadPdf = useCallback(
    (report: MonitoringReport) => {
      const threadId = getThreadId();
      const variables = {
        report: buildReportPdfPayload(
          report,
          proposedChecks,
          user!,
          accountUsage,
          formData,
          recommendedChecks,
          positionById,
          proposedGroups
        ),
        ...(threadId && {thread_id: threadId}),
      };
      openPdfDownload(
        () =>
          fetchPdf({variables}).then(({data, error}) => {
            if (error) {
              throw error;
            }
            return data?.analyzerReportPdf;
          }),
        toast.error
      );
    },
    [
      fetchPdf,
      proposedChecks,
      recommendedChecks,
      positionById,
      proposedGroups,
      getThreadId,
      user,
      accountUsage,
      formData,
    ]
  );

  return {downloadPdf, loading};
};
